import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { buildPmsServices, type PmsServices } from '@lemontaigu/pms-adapters';
import { fixedScope, type Property, type PropertyCode, type QueryScope } from '@lemontaigu/pms-core';
import { useCapabilities } from '@lemontaigu/platform-ui';
import { pickProperty } from './logic.js';
import { supabase } from './supabase.js';
import { PMS_API_BASE } from './config.js';

// The PMS composition root (PMS-P2-ARCHITECTURE-BRIEF §7) — shared by the
// pms.lemontaigu.com app and the bos.* superset mount (source-exported, one
// definition). This is the ONE place that names the concrete Supabase client
// for the PMS domain: it builds the driving services from it and hands them
// down. PMS tabs call usePms() and depend only on the pms-core service
// interfaces — never on the supabase singleton directly.

const PmsContext = createContext<PmsServices | null>(null);

/** The établissement dimension. Separate from PmsContext because tabs need the
 *  SERVICES — a tab that reads the current property is usually a tab that
 *  should have let its repository do the scoping.
 *
 *  There is no `select` since U4 (PMS-UNIFIED-DESK-BRIEF §8): the header
 *  switcher is gone. What you see is your reach; where you write is the
 *  object's (M11′). `current` is the DEFAULT établissement — the first in
 *  presentation order — that a page about one établissement opens on
 *  (`PerEtablissement`), never a global scope that someone switches. */
export interface PmsScope {
  properties: Property[];
  current: PropertyCode | null;
  /** True while the property list is still being read. The services work
   *  throughout — an unresolved scope means "let the database decide". */
  loading: boolean;
}

const PmsScopeContext = createContext<PmsScope | null>(null);

/** One établissement's services, bound to it for good (U2,
 *  PMS-UNIFIED-DESK-BRIEF §4.1). The browser twin of the service-role map the
 *  job runner builds per property (multi-property build brief, step 6). */
export interface PmsBundle {
  property: Property;
  services: PmsServices;
}

/** Every établissement in the actor's reach, in presentation order, each with
 *  its own bundle. Unlike `PmsContext` this is never re-scoped: a unified
 *  screen reads each building through its own bundle and writes
 *  through the bundle of the row it acts on, so « which établissement » is a
 *  lookup that cannot be skipped rather than a parameter someone must remember. */
const PmsReachContext = createContext<readonly PmsBundle[] | null>(null);

/** « Exploitation » or « Formation » (operator, 2026-11-02): which side of the
 *  house the app shows. Exploitation = the real établissements (HOTEL, its data
 *  from Vega); Formation = the test ones (`mode = 'training'`: FORMATION, and
 *  4AGUERES as the Channex pre-prod). Never both on one screen. Filtering the
 *  reach here, once, is what makes every screen follow — they all read it. */
export type PmsEnvironment = 'exploitation' | 'formation';

export interface PmsEnvironmentState {
  env: PmsEnvironment;
  setEnv: (env: PmsEnvironment) => void;
  /** False when the actor reaches no test établissement: no switch is shown. */
  available: boolean;
}

const PmsEnvironmentContext = createContext<PmsEnvironmentState | null>(null);
const ENV_KEY = 'pms.environment';

function readEnv(): PmsEnvironment {
  try {
    return window.localStorage.getItem(ENV_KEY) === 'formation' ? 'formation' : 'exploitation';
  } catch {
    return 'exploitation';
  }
}

/** The établissements one side shows. With no test établissement in reach, all. */
export function propertiesFor(env: PmsEnvironment, list: readonly Property[]): Property[] {
  const hasTraining = list.some((p) => p.mode === 'training');
  if (!hasTraining) return [...list];
  return list.filter((p) => (p.mode === 'training') === (env === 'formation'));
}

// U4 removed the remembered choice (`localStorage` key `pms.property`, P1.6
// step 1) with the switcher that wrote it. A key left in a browser is no
// longer read: a stale « Appartements » must not become a desk's default once
// nothing on screen can change it back.

export function PmsProvider({
  children,
  chrome,
}: {
  children: ReactNode;
  /** App chrome rendered INSIDE the contexts but OUTSIDE the gate below — the
   *  nav, in practice (docs/UI-BRANDING.md B1, 2026-08-13).
   *
   *  It was introduced because the account menu carried the établissement
   *  list, so the header had to call `usePmsScope()`. That list went at U4
   *  (PMS-UNIFIED-DESK-BRIEF §8); the slot stays for the reason it still
   *  serves: the nav is NOT hidden while the property list loads. */
  chrome?: ReactNode;
}) {
  // The L2 gate's actor source (ADR-0013 §4). Through a ref, deliberately: the
  // services are built ONCE, while the capability set arrives asynchronously
  // and changes on `refresh()`. Passing `actor` by value would freeze the gate
  // at ANONYMOUS — the fail-closed loading state — for the whole session, so
  // every call would be denied. The ref keeps `current()` live without making
  // the services depend on a value that changes identity on every fetch.
  const { actor } = useCapabilities();
  const actorRef = useRef(actor);
  actorRef.current = actor;

  // The scope source, and a ref for the same reason (P1.5 step 7): the
  // établissement resolves asynchronously — it is a database read — after the
  // services are built. Since U4 it is set once, to the default établissement,
  // and nothing changes it.
  //
  // It starts as `actor`: until the list arrives, the database decides. RLS
  // still narrows every read and the RPCs still resolve their property from
  // `actor_default_property()`, so this is the behaviour that shipped through
  // all of P1.5 — the safe resting state, not a gap.
  const scopeRef = useRef<QueryScope>({ kind: 'actor' });

  // Built once and shared by every service set below, so the scoped services
  // and the per-établissement bundles read the same live actor and token.
  const actorSource = useMemo(() => ({ current: () => actorRef.current }), []);
  // The channel controls (P2). The token is read AT CALL TIME, not captured
  // here: a PMS session outlives a JWT, so a token taken at mount starts
  // answering 401 mid-afternoon while the screen still looks logged in. This
  // closure is the only place in the browser that knows a sync is an HTTP call
  // at all — `ChannexMapping` sees a port.
  const channel = useMemo(
    () => ({
      apiBase: PMS_API_BASE,
      accessToken: async () =>
        (await supabase.auth.getSession()).data.session?.access_token ?? null,
    }),
    [],
  );

  const services = useMemo(
    () => buildPmsServices(supabase, actorSource, { current: () => scopeRef.current }, channel),
    [actorSource, channel],
  );

  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [env, setEnvState] = useState<PmsEnvironment>(readEnv);
  const setEnv = (next: PmsEnvironment) => {
    try {
      window.localStorage.setItem(ENV_KEY, next);
    } catch {
      // A blocked storage only means the choice is not remembered.
    }
    setEnvState(next);
  };
  const properties = useMemo(() => propertiesFor(env, allProperties), [env, allProperties]);
  const available = allProperties.some((p) => p.mode === 'training');

  // U2: one bundle per établissement in reach, each bound with a FIXED scope.
  // Building a bundle is plain object construction (no subscription, no
  // timer), so one per établissement costs nothing worth measuring.
  const reach = useMemo<readonly PmsBundle[]>(
    () =>
      properties.map((property) => ({
        property,
        services: buildPmsServices(
          supabase,
          actorSource,
          fixedScope({ kind: 'property', propertyCode: property.code, tenantCode: property.tenantCode }),
          channel,
        ),
      })),
    [properties, actorSource, channel],
  );
  const [current, setCurrent] = useState<PropertyCode | null>(null);
  const [loading, setLoading] = useState(true);

  const applyScope = (list: Property[], code: PropertyCode | null) => {
    const chosen = list.find((p) => p.code === code);
    scopeRef.current = chosen
      ? { kind: 'property', propertyCode: chosen.code, tenantCode: chosen.tenantCode }
      : { kind: 'actor' };
    setCurrent(chosen ? chosen.code : null);
  };

  useEffect(() => {
    let cancelled = false;
    void services.properties.listForActor().then((r) => {
      if (cancelled) return;
      // A failed read leaves the scope at `actor`, which still works. Refusing
      // to render the PMS because the list could not load would be a worse
      // outcome than rendering it exactly as it behaved yesterday.
      const list = r.ok ? r.value : [];
      setAllProperties(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [services]);

  // Bind to a concrete établissement of the side shown whenever there is one —
  // the fix for the ambiguity cliff (P1.6 build brief §3): with two in reach,
  // `actor_default_property()` is NULL and an unscoped RPC raises
  // « établissement non résolu » rather than guessing.
  useEffect(() => {
    applyScope(properties, pickProperty(properties));
  }, [properties]);

  const scope = useMemo<PmsScope>(() => ({ properties, current, loading }), [properties, current, loading]);
  const envState = useMemo<PmsEnvironmentState>(
    () => ({ env, setEnv, available }),
    // setEnv only writes state and storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [env, available],
  );

  return (
    <PmsContext.Provider value={services}>
     <PmsEnvironmentContext.Provider value={envState}>
      <PmsScopeContext.Provider value={scope}>
        <PmsReachContext.Provider value={reach}>
          {/* « Exploitation » / « Formation », and the band that says which. */}
          <EnvironmentBar />
          {/* The shell — see `chrome`. */}
          {chrome}
          {/* Gated on `!loading` so the first mount already has the resolved
              scope and the bundles: a tab that fetched before the list arrived
              would read as `actor`, which two établissements in reach refuse.

              No key since U4. Until then the subtree was keyed on the
              switcher's établissement, so a switch REMOUNTED every tab — the
              chat and the team board included. With the switcher gone there
              is nothing to switch, and a page about one établissement makes
              its choice inside the page (`PerEtablissement`), remounting only
              itself. */}
          {/* Keyed on the side: switching remounts every tab, so nothing read
              on one side is left on screen on the other. Also wait for the
              scope to follow the side, or a tab would read the other side. */}
          {!loading && (properties.length === 0 || properties.some((p) => p.code === current)) && (
            <Fragment key={env}>{children}</Fragment>
          )}
        </PmsReachContext.Provider>
      </PmsScopeContext.Provider>
     </PmsEnvironmentContext.Provider>
    </PmsContext.Provider>
  );
}

/** Every établissement in reach with its own bundle, in presentation order
 *  (U2). Empty until the property list has been read. */
export function usePmsReach(): readonly PmsBundle[] {
  const ctx = useContext(PmsReachContext);
  if (!ctx) throw new Error('usePmsReach must be used within <PmsProvider>');
  return ctx;
}

/** As `usePmsReach`, but empty outside a provider — for leaf components (a
 *  row's établissement tag) that their tests render bare. Empty reads as « one
 *  établissement or none », which shows no tag: the single-building screen. */
export function usePmsReachOrEmpty(): readonly PmsBundle[] {
  return useContext(PmsReachContext) ?? [];
}

/** The bundle of ONE établissement — the one a row belongs to, for a write that
 *  acts on it. `null` when that établissement is not in reach: the caller says
 *  so rather than writing through another building's services. */
export function usePmsFor(code: PropertyCode): PmsServices | null {
  return usePmsReach().find((b) => b.property.code === code)?.services ?? null;
}

export function usePms(): PmsServices {
  const ctx = useContext(PmsContext);
  if (!ctx) throw new Error('usePms must be used within <PmsProvider>');
  return ctx;
}

export function usePmsScope(): PmsScope {
  const ctx = useContext(PmsScopeContext);
  if (!ctx) throw new Error('usePmsScope must be used within <PmsProvider>');
  return ctx;
}

/**
 * A page about ONE établissement — Tarifs, the books, the statistics, the
 * card controls — with the choice of which made INSIDE the page (U3, M11′:
 * figures stay per établissement; choosing one is a page's filter, never a
 * global scope).
 *
 * It re-provides that établissement's bundle as `usePms()` and its code as
 * `usePmsScope().current` to the page, so the page itself does not change, and
 * remounts the page when the choice changes — an open fiche or a selected
 * calendar belongs to the building being left. It opens on the default
 * établissement, the first in presentation order. With one établissement in
 * reach it adds nothing at all.
 */
export function PerEtablissement({ children }: { children: ReactNode }) {
  const reach = usePmsReach();
  const scope = usePmsScope();
  const [chosen, setChosen] = useState<PropertyCode | null>(null);
  const bundle = reach.find((b) => b.property.code === (chosen ?? scope.current)) ?? reach[0] ?? null;
  const pageScope = useMemo<PmsScope>(
    () => ({ ...scope, current: bundle?.property.code ?? scope.current }),
    [scope, bundle],
  );
  if (reach.length < 2 || bundle === null) return <>{children}</>;
  return (
    <>
      <div className="pms-etab-choice" data-testid="page-etablissement">
        <label>
          Établissement
          <select value={bundle.property.code} onChange={(e) => setChosen(e.target.value)}>
            {reach.map((b) => (
              <option key={b.property.code} value={b.property.code}>
                {b.property.labelFr}
              </option>
            ))}
          </select>
        </label>
      </div>
      <PmsContext.Provider value={bundle.services}>
        <PmsScopeContext.Provider value={pageScope}>
          <Fragment key={bundle.property.code}>{children}</Fragment>
        </PmsScopeContext.Provider>
      </PmsContext.Provider>
    </>
  );
}

/** « Exploitation » / « Formation » — null outside a provider. */
export function usePmsEnvironment(): PmsEnvironmentState | null {
  return useContext(PmsEnvironmentContext);
}

/** The switch, and in Formation the band across every screen. Nothing at all
 *  for an actor who reaches no test établissement. */
export function EnvironmentBar() {
  const state = usePmsEnvironment();
  if (!state?.available) return null;
  const { env, setEnv } = state;
  return (
    <div className={`pms-env-bar ${env === 'formation' ? 'is-formation' : ''}`} data-testid="pms-env-bar">
      {env === 'formation' && <strong className="pms-env-label">FORMATION — données de test</strong>}
      <div className="pms-env-switch" role="group" aria-label="Environnement">
        <button
          type="button"
          className={env === 'exploitation' ? 'is-on' : ''}
          aria-pressed={env === 'exploitation'}
          onClick={() => setEnv('exploitation')}
        >
          Exploitation
        </button>
        <button
          type="button"
          className={env === 'formation' ? 'is-on' : ''}
          aria-pressed={env === 'formation'}
          onClick={() => setEnv('formation')}
        >
          Formation
        </button>
      </div>
    </div>
  );
}
